import React, { useState, useEffect } from 'react';
import { Users, Star, TrendingUp, Edit, Trash2, Plus } from 'lucide-react';
import barrelReplenishmentService from '../../../services/barrelReplenishmentService';

const SupplierManagementPanel = ({ onRefresh }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    loadSuppliers();
  }, []);

  const loadSuppliers = async () => {
    try {
      setLoading(true);
      const data = await barrelReplenishmentService?.getSuppliers();
      setSuppliers(data);
    } catch (error) {
      console.error('Error loading suppliers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (editingSupplier) {
        await barrelReplenishmentService?.updateSupplier(editingSupplier?.id, formData);
      } else {
        await barrelReplenishmentService?.createSupplier(formData);
      }
      await loadSuppliers();
      setEditingSupplier(null);
      setShowAddForm(false);
      setFormData({});
      onRefresh?.();
    } catch (error) {
      console.error('Error saving supplier:', error);
      alert('Erreur lors de la sauvegarde du fournisseur');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Voulez-vous vraiment supprimer ce fournisseur ?')) return;
    try {
      await barrelReplenishmentService?.deleteSupplier(id);
      await loadSuppliers();
      onRefresh?.();
    } catch (error) {
      console.error('Error deleting supplier:', error);
      alert('Erreur lors de la suppression du fournisseur');
    }
  };

  const startEdit = (supplier) => {
    setEditingSupplier(supplier);
    setFormData(supplier);
    setShowAddForm(false);
  };

  const startAdd = () => {
    setShowAddForm(true);
    setEditingSupplier(null);
    setFormData({
      name: '',
      contact_person: '',
      phone: '',
      email: '',
      city: '',
      noir_270l_price: 35.00,
      bleu_220l_price: 40.00,
      minimum_order_quantity: 10,
      delivery_time_days: 7,
      quality_rating: 5.00,
      reliability_score: 5.00,
      payment_terms: 'Net 30',
      is_active: true
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-24 bg-gray-200 rounded"></div>
            <div className="h-24 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Users className="w-5 h-5 text-purple-600" />
          Gestion des Fournisseurs
        </h2>
        <button
          onClick={startAdd}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Ajouter Fournisseur
        </button>
      </div>
      {(showAddForm || editingSupplier) && (
        <div className="mb-6 p-4 border-2 border-purple-200 rounded-lg bg-purple-50">
          <h3 className="font-semibold text-gray-900 mb-3">
            {editingSupplier ? 'Modifier Fournisseur' : 'Nouveau Fournisseur'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
              <input
                type="text"
                value={formData?.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact</label>
              <input
                type="text"
                value={formData?.contact_person || ''}
                onChange={(e) => setFormData({ ...formData, contact_person: e?.target?.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone *</label>
              <input
                type="tel"
                value={formData?.phone || ''}
                onChange={(e) => setFormData({ ...formData, phone: e?.target?.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={formData?.email || ''}
                onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
              <input
                type="text"
                value={formData?.city || ''}
                onChange={(e) => setFormData({ ...formData, city: e?.target?.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prix Noir 270L (€)</label>
              <input
                type="number"
                step="0.01"
                value={formData?.noir_270l_price || ''}
                onChange={(e) => setFormData({ ...formData, noir_270l_price: parseFloat(e?.target?.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prix Bleu 220L (€)</label>
              <input
                type="number"
                step="0.01"
                value={formData?.bleu_220l_price || ''}
                onChange={(e) => setFormData({ ...formData, bleu_220l_price: parseFloat(e?.target?.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quantité Min. Commande</label>
              <input
                type="number"
                value={formData?.minimum_order_quantity || ''}
                onChange={(e) => setFormData({ ...formData, minimum_order_quantity: parseInt(e?.target?.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Délai Livraison (jours)</label>
              <input
                type="number"
                value={formData?.delivery_time_days || ''}
                onChange={(e) => setFormData({ ...formData, delivery_time_days: parseInt(e?.target?.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Conditions de Paiement</label>
              <input
                type="text"
                value={formData?.payment_terms || ''}
                onChange={(e) => setFormData({ ...formData, payment_terms: e?.target?.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData?.is_active || false}
                  onChange={(e) => setFormData({ ...formData, is_active: e?.target?.checked })}
                  className="w-4 h-4 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                />
                <span className="text-sm text-gray-700">Fournisseur actif</span>
              </label>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Sauvegarder
            </button>
            <button
              onClick={() => {
                setEditingSupplier(null);
                setShowAddForm(false);
                setFormData({});
              }}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
            >
              Annuler
            </button>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {suppliers?.map((supplier) => (
          <div
            key={supplier?.id}
            className={`border-2 rounded-lg p-4 ${
              supplier?.is_active ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'
            }`}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-semibold text-gray-900">{supplier?.name}</h3>
                <p className="text-sm text-gray-600">{supplier?.contact_person}</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => startEdit(supplier)}
                  className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(supplier?.id)}
                  className="p-1 text-red-600 hover:bg-red-100 rounded"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Téléphone:</span>
                <span className="font-medium text-gray-900">{supplier?.phone}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Ville:</span>
                <span className="font-medium text-gray-900">{supplier?.city || 'N/A'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Noir 270L:</span>
                <span className="font-medium text-gray-900">{supplier?.noir_270l_price?.toFixed(2)}€</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Bleu 220L:</span>
                <span className="font-medium text-gray-900">{supplier?.bleu_220l_price?.toFixed(2)}€</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Délai livraison:</span>
                <span className="font-medium text-gray-900">{supplier?.delivery_time_days} jours</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                <span className="text-gray-600">Qualité:</span>
                <span className="font-medium text-gray-900">{supplier?.quality_rating?.toFixed(1)}/5</span>
              </div>
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-green-500" />
                <span className="text-gray-600">Fiabilité:</span>
                <span className="font-medium text-gray-900">{supplier?.reliability_score?.toFixed(1)}/5</span>
              </div>
            </div>

            {supplier?.is_active ? (
              <span className="inline-block mt-3 px-3 py-1 bg-green-600 text-white text-xs font-semibold rounded-full">
                Actif
              </span>
            ) : (
              <span className="inline-block mt-3 px-3 py-1 bg-gray-400 text-white text-xs font-semibold rounded-full">
                Inactif
              </span>
            )}
          </div>
        ))}
      </div>
      {suppliers?.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <Users className="w-12 h-12 mx-auto mb-2 text-gray-400" />
          <p>Aucun fournisseur configuré</p>
        </div>
      )}
    </div>
  );
};

export default SupplierManagementPanel;